/**
 * AI Assistant Panel (New - Using Shared Components)
 * 
 * Synthora Hub AI Assistant using the unified shared component architecture.
 * This is the new version that uses shared styles while keeping the existing context.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { useAuth0 } from "@auth0/auth0-react";
import { useAIAssistant } from './AIAssistantContext';
import { MODULES, getContextualHelp } from './synthoraKnowledge';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import AIAssistantSettings from './AIAssistantSettings';
import { useToast } from '../../Context/ToastContext';

// Import shared styled components
import {
  PanelOverlay,
  FloatingPanelContainer,
  PanelHeader,
  HeaderTitle,
  HeaderIcon,
  TitleText,
  HeaderActions,
  IconButton,
  PinButton,
  SettingsButton,
  ContextBadge,
  MessagesContainer,
  MessageGroup,
  MessageBubble,
  TypingIndicator,
  SuggestionsContainer,
  SuggestionButton,
  InputContainer,
  InputWrapper,
  TextInput,
  SendButton,
  MicButton,
  RecordingDot,
  RecordingTimer,
  TranscribingIndicator,
  HelpTip,
  WelcomeMessage,
  ProactiveSuggestionBanner,
  SuggestionAction,
} from '../SharedAssistant/AssistantStyles';

// Import SVG icon
import HubAssistantIcon from '../../Images/hubAssistant.svg';

/**
 * Parse markdown text to HTML
 */
const parseMarkdown = (text) => {
  if (!text) return '';
  
  const paragraphs = text.split(/\n\n+/);
  
  const parsedParagraphs = paragraphs.map(paragraph => {
    let p = paragraph.trim();
    if (!p) return '';
    
    if (/^[-*_]{3,}$/.test(p)) {
      return '<hr class="md-hr"/>';
    }
    
    if (p.startsWith('### ')) {
      return `<div class="md-h3">${p.slice(4)}</div>`;
    }
    if (p.startsWith('## ')) {
      return `<div class="md-h2">${p.slice(3)}</div>`;
    }
    if (p.startsWith('# ')) {
      return `<div class="md-h1">${p.slice(2)}</div>`;
    }
    
    const lines = p.split('\n');
    const isNumberedList = lines.every(line => /^\d+[\.\)]\s/.test(line.trim()) || line.trim() === '');
    const isBulletList = lines.every(line => /^[-•]\s/.test(line.trim()) || line.trim() === '');
    
    if (isNumberedList && lines.some(line => /^\d+[\.\)]\s/.test(line.trim()))) {
      const items = lines
        .filter(line => line.trim())
        .map(line => {
          const content = line.replace(/^\d+[\.\)]\s*/, '').trim();
          const formatted = content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/`(.*?)`/g, '<code>$1</code>');
          return `<li>${formatted}</li>`;
        })
        .join('');
      return `<ol>${items}</ol>`;
    }
    
    if (isBulletList && lines.some(line => /^[-•]\s/.test(line.trim()))) {
      const items = lines
        .filter(line => line.trim())
        .map(line => {
          const content = line.replace(/^[-•]\s*/, '').trim();
          const formatted = content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/`(.*?)`/g, '<code>$1</code>');
          return `<li>${formatted}</li>`;
        })
        .join('');
      return `<ul>${items}</ul>`;
    }
    
    let formatted = p
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/`(.*?)`/g, '<code>$1</code>')
      .replace(/\n/g, '<br/>');
    
    return `<p>${formatted}</p>`;
  });
  
  return parsedParagraphs.filter(p => p).join('');
};

const AIAssistantPanelNew = () => {
  const authenticatedAxios = useAuthenticatedAxios();
  const { user } = useAuth0();
  const { showToast } = useToast();
  const {
    isPanelOpen,
    togglePanel,
    closePanel,
    screenContext,
    conversationHistory,
    addMessage,
    clearConversation,
    isLoading,
    setIsLoading,
    getFullContext,
    showSettingsPanel,
    toggleSettingsPanel,
    settings,
    proactiveSuggestion,
    showProactiveSuggestion,
    dismissProactiveSuggestion,
    isPinned,
    togglePin,
    unpinAndClose,
  } = useAIAssistant();

  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  
  // Speech-to-text state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingMimeType, setRecordingMimeType] = useState('audio/webm');
  const timerRef = useRef(null);

  // Get contextual help
  const contextualHelp = getContextualHelp(screenContext);
  const currentModule = MODULES[screenContext.currentModule];

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversationHistory]);

  // Focus input when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isPanelOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        togglePanel();
      }
      if (e.key === 'Escape' && isPanelOpen) {
        closePanel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPanelOpen, togglePanel, closePanel]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const toggleRecording = async () => {
    if (isRecording) {
      if (mediaRecorder) {
        mediaRecorder.stop();
        clearInterval(timerRef.current);
        setRecordingTime(0);
      }
      setIsRecording(false);
    } else {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, sampleRate: 16000, echoCancellation: true, noiseSuppression: true }
        });

        let mimeType = 'audio/webm';
        if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
          mimeType = 'audio/webm;codecs=opus';
        } else if (MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
          mimeType = 'audio/ogg;codecs=opus';
        }

        setRecordingMimeType(mimeType);
        const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 128000 });
        const currentChunks = [];

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) currentChunks.push(e.data);
        };

        recorder.onstop = () => transcribeAudio(stream, currentChunks);

        recorder.start(1000);
        setMediaRecorder(recorder);
        setIsRecording(true);

        timerRef.current = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
      } catch (error) {
        console.error('Error accessing microphone:', error);
        showToast('Could not access your microphone.', 'error');
      }
    }
  };

  const transcribeAudio = async (stream, chunks) => {
    try {
      stream.getTracks().forEach(track => track.stop());
      setIsTranscribing(true);

      const audioBlob = new Blob(chunks, { type: recordingMimeType });
      const ext = recordingMimeType.includes('webm') ? 'webm' : recordingMimeType.includes('ogg') ? 'ogg' : 'mp4';
      
      const formData = new FormData();
      formData.append('file', audioBlob, `recording_${Date.now()}.${ext}`);

      const response = await authenticatedAxios.post('/api/process_audio', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'X-Expert-ID': user?.sub || '', // Required for Whisper billing
        },
        timeout: 60000
      });

      if (response.data?.transcription?.trim()) {
        setInput(prev => prev + (prev.length > 0 ? ' ' : '') + response.data.transcription);
        inputRef.current?.focus();
      } else {
        showToast("Couldn't detect any speech.", "warning");
      }
    } catch (error) {
      console.error('Error during transcription:', error);
      showToast('Error transcribing audio.', 'error');
    } finally {
      setIsTranscribing(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleSendMessage = useCallback(async (messageText = null) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    addMessage({ role: 'user', content: text });
    setInput('');
    setShowSuggestions(false);
    setIsLoading(true);

    try {
      const fullContext = getFullContext();
      
      const response = await authenticatedAxios.post('/synthora/assistant', {
        message: text,
        context: fullContext,
        conversationHistory: conversationHistory.slice(-10)
      });

      addMessage({
        role: 'assistant',
        content: response.data.message || "I'm sorry, I couldn't process that request."
      });

    } catch (error) {
      console.error('Error sending message:', error);
      let fallbackMessage = "I'm having trouble connecting right now. ";
      if (currentModule) {
        fallbackMessage += `But I can tell you're in **${currentModule.name}**. ${currentModule.purpose}`;
      }
      addMessage({ role: 'assistant', content: fallbackMessage });
    } finally {
      setIsLoading(false);
    }
  }, [input, isLoading, addMessage, setIsLoading, getFullContext, authenticatedAxios, conversationHistory, currentModule]);

  const handleSuggestionClick = (suggestion) => handleSendMessage(suggestion);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  return (
    <>
      <PanelOverlay $isOpen={isPanelOpen} $isPinned={isPinned} onClick={closePanel} />

      <FloatingPanelContainer $isOpen={isPanelOpen}>
        <PanelHeader>
          <HeaderTitle>
            <HeaderIcon>
              <img src={HubAssistantIcon} alt="" />
            </HeaderIcon>
            <TitleText>
              <h3>Synthora Assistant</h3>
              <span>AI-powered help for your setup</span>
            </TitleText>
          </HeaderTitle>
          <HeaderActions>
            <PinButton onClick={togglePin} title={isPinned ? "Unpin" : "Pin"} $isPinned={isPinned}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
            </PinButton>
            <SettingsButton onClick={toggleSettingsPanel} title="Settings" $active={showSettingsPanel}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
            </SettingsButton>
            <IconButton onClick={clearConversation} title="Clear">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z"/>
              </svg>
            </IconButton>
            <IconButton onClick={isPinned ? unpinAndClose : closePanel} title="Close">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </IconButton>
          </HeaderActions>
        </PanelHeader>

        {showSettingsPanel ? (
          <AIAssistantSettings />
        ) : (
          <>
            {currentModule && (
              <ContextBadge>
                <span className="indicator" />
                <span>Currently in:</span>
                <span className="text">{currentModule.name}</span>
              </ContextBadge>
            )}

            {showProactiveSuggestion && proactiveSuggestion && settings.proactiveSuggestions && (
              <ProactiveSuggestionBanner $type={proactiveSuggestion.type}>
                <div className="icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4M12 8h.01"/>
                  </svg>
                </div>
                <div className="content">
                  <div className="text">{proactiveSuggestion.text}</div>
                  <div className="actions">
                    <SuggestionAction $primary onClick={() => handleSendMessage(proactiveSuggestion.text)}>
                      Ask about this
                    </SuggestionAction>
                    <SuggestionAction onClick={dismissProactiveSuggestion}>Dismiss</SuggestionAction>
                  </div>
                </div>
              </ProactiveSuggestionBanner>
            )}

            <MessagesContainer>
              {conversationHistory.length === 0 ? (
                <>
                  <WelcomeMessage>
                    <div className="icon-wrapper">
                      <img src={HubAssistantIcon} alt="" />
                    </div>
                    <h4>Hi! I'm your Synthora Assistant</h4>
                    <p>I can help you understand and use Synthora Hub. Ask me anything about setting up agents, creating flows, or managing your knowledge base.</p>
                  </WelcomeMessage>
                  
                  {showSuggestions && (
                    <SuggestionsContainer>
                      {contextualHelp.suggestions.map((suggestion, idx) => (
                        <SuggestionButton key={idx} onClick={() => handleSuggestionClick(suggestion)}>
                          {suggestion}
                        </SuggestionButton>
                      ))}
                    </SuggestionsContainer>
                  )}
                </>
              ) : (
                <>
                  {conversationHistory.map((msg, idx) => (
                    <MessageGroup key={idx}>
                      <MessageBubble 
                        $isUser={msg.role === 'user'}
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(msg.role === 'user' ? msg.content : parseMarkdown(msg.content))
                        }}
                      />
                    </MessageGroup>
                  ))}
                  
                  {isLoading && (
                    <TypingIndicator>
                      <span /><span /><span />
                    </TypingIndicator>
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </MessagesContainer>

            <InputContainer>
              {isTranscribing && (
                <TranscribingIndicator>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
                  </svg>
                  Transcribing your voice...
                </TranscribingIndicator>
              )}
              <InputWrapper>
                <TextInput
                  ref={inputRef}
                  value={input}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder={isRecording ? "Recording... click mic to stop" : "Ask me anything about Synthora..."}
                  disabled={isLoading || isRecording}
                  rows={1}
                />
                
                <MicButton
                  onClick={toggleRecording}
                  disabled={isLoading || isTranscribing}
                  $isRecording={isRecording}
                  title={isRecording ? "Stop" : "Voice input"}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2a4 4 0 0 1 4 4v4a4 4 0 0 1-8 0V6a4 4 0 0 1 4-4z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="22"/>
                    <line x1="8" y1="22" x2="16" y2="22"/>
                  </svg>
                  {isRecording && (
                    <>
                      <RecordingDot />
                      <RecordingTimer>{formatTime(recordingTime)}</RecordingTimer>
                    </>
                  )}
                </MicButton>
                
                <SendButton onClick={() => handleSendMessage()} disabled={!input.trim() || isLoading || isRecording}>
                  ↑
                </SendButton>
              </InputWrapper>
              <HelpTip>
                <kbd>⌘K</kbd> to toggle • <kbd>🎤</kbd> for voice • <kbd>Enter</kbd> to send
              </HelpTip>
            </InputContainer>
          </>
        )}
      </FloatingPanelContainer>
    </>
  );
};

export default AIAssistantPanelNew;

